import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import {
  legalAnalyticsService,
  LEGAL_ANALYTICS_RANGE_OPTIONS,
  type LegalAnalyticsData,
  type LegalAnalyticsRange,
  type SlaStatus,
  type StatusBreakdown,
} from '@/services/legal-analytics.service';

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

const SLA_STYLES: Record<SlaStatus, string> = {
  good: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  critical: 'text-danger-600 dark:text-danger-400',
};

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function BreakdownCard({ breakdown }: { breakdown: StatusBreakdown }) {
  const entries = Object.entries(breakdown.data).sort(([, a], [, b]) => b - a);
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="font-semibold text-secondary-900 dark:text-white mb-4">{breakdown.title}</h3>
        {entries.length > 0 ? (
          <div className="space-y-3">
            {entries.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">{humanize(key)}</span>
                <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                  {formatNumber(value)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-secondary-500 dark:text-secondary-400">No data available</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function LegalAnalytics() {
  const [range, setRange] = useState<LegalAnalyticsRange>('MONTH');
  const [data, setData] = useState<LegalAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (selected: LegalAnalyticsRange) => {
    try {
      setLoading(true);
      const result = await legalAnalyticsService.getAnalytics(selected);
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Failed to load L&C analytics:', err instanceof Error ? err.message : err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData(range);
  }, [fetchData, range]);

  const handleExport = useCallback(() => {
    if (!data) {return;}
    const csv = legalAnalyticsService.exportCsv(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `legal-compliance-analytics-${range.toLowerCase()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [data, range]);

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header + controls */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Legal &amp; Compliance Analytics
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Trends and SLA indicators across vendors, matters, compliance, and HIPAA incidents
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/legal/dashboard">
              <Button variant="outline">
                <Squares2X2Icon className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <label htmlFor="lc-analytics-range" className="sr-only">
              Time range
            </label>
            <select
              id="lc-analytics-range"
              value={range}
              onChange={(e) => setRange(e.target.value as LegalAnalyticsRange)}
              className="h-10 rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-700 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-200"
            >
              {LEGAL_ANALYTICS_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Button variant="outline" onClick={handleExport} disabled={!data}>
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </motion.div>

      {loading ? (
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardContent className="p-12 text-center text-secondary-600 dark:text-secondary-400">
              Loading analytics data...
            </CardContent>
          </Card>
        </motion.div>
      ) : error || !data ? (
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Analytics
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {error ?? 'Failed to load analytics data'}
              </p>
              <Button onClick={() => fetchData(range)} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <>
          {data.failedSources.length > 0 && (
            <motion.div variants={ITEM_VARIANTS}>
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 dark:border-warning-900/40 dark:bg-warning-900/20 p-3 text-sm text-warning-800 dark:text-warning-200"
              >
                <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                <span>Some data couldn&apos;t be loaded and is shown as zero. Try a refresh.</span>
              </div>
            </motion.div>
          )}

          {/* Metric cards */}
          <motion.div variants={ITEM_VARIANTS}>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {data.metrics.map((metric) => (
                <Card key={metric.key}>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {metric.format === 'currency'
                        ? formatCurrency(metric.value)
                        : formatNumber(metric.value)}
                    </div>
                    <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                      {metric.label}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* SLA / overdue indicators */}
          <motion.div variants={ITEM_VARIANTS}>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
              SLA &amp; Overdue Indicators
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.sla.map((indicator) => (
                <Card key={indicator.key}>
                  <CardContent className="p-5 flex items-center justify-between">
                    <span className="text-sm text-secondary-600 dark:text-secondary-400 pr-3">
                      {indicator.label}
                    </span>
                    <span className={cn('text-2xl font-bold', SLA_STYLES[indicator.status])}>
                      {formatNumber(indicator.value)}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* Trend */}
          <motion.div variants={ITEM_VARIANTS}>
            <Card>
              <CardContent className="p-5">
                <h3 className="font-semibold text-secondary-900 dark:text-white mb-4">
                  HIPAA Incident Volume ({humanize(data.trend.granularity)})
                </h3>
                {data.trend.points.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data.trend.points}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} name="Incidents" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 py-12 text-center">
                    No trend data available for the selected range.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Status breakdowns */}
          <motion.div variants={ITEM_VARIANTS}>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
              Status Breakdowns
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.breakdowns.map((breakdown) => (
                <BreakdownCard key={breakdown.key} breakdown={breakdown} />
              ))}
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
